import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FilterService } from 'src/app/filter/filter.service';
import { PAMPRestAPIService } from 'src/app/core/api/pamp-rest-api.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { FilterType, Patient } from 'src/API';
import { OrderRequest } from 'src/app/core/models/order-request.model';
import { CheckinData } from 'src/app/core/models/patient-app-widget.model';

export interface PatientAppWidgetData {
  patients: Patient[];
  productOrderStats: { allOrders: Partial<OrderRequest>[] };
  checkinStats: CheckinData[];
}

@Injectable({
  providedIn: 'root'
})
export class PatientAppWidgetDataService {
  private dataSubject = new ReplaySubject<PatientAppWidgetData>(1);
  public data$ = this.dataSubject.asObservable();
  private isFetching = false;

  constructor(
    private filterService: FilterService,
    private pampRestApiService: PAMPRestAPIService,
    private clinicSetupService: ClinicSetupService
  ) {}

  public loadData(): void {
    if (this.isFetching) {
      return;
    }
    this.isFetching = true;

    const clinicId = this.clinicSetupService.clinicId;
    const productOrders$ = clinicId
      ? this.pampRestApiService
          .getProductOrderStats(clinicId)
          .pipe(catchError(() => of({ allOrders: [] })))
      : of({ allOrders: [] });
    const checkins$ = clinicId
      ? this.pampRestApiService.getCheckinStats(clinicId).pipe(catchError(() => of([])))
      : of([]);

    forkJoin([
      this.filterService.queryFilterData(FilterType.patient, null).pipe(catchError(() => of([]))),
      productOrders$,
      checkins$
    ]).subscribe(
      ([patients, productOrderStats, checkinStats]) => {
        this.dataSubject.next({
          patients: patients as Patient[],
          productOrderStats: productOrderStats as { allOrders: Partial<OrderRequest>[] },
          checkinStats: checkinStats as CheckinData[]
        });
        this.isFetching = false;
      },
      error => {
        console.error('Error loading patient app widget data', error);
        this.dataSubject.next({
          patients: [],
          productOrderStats: { allOrders: [] },
          checkinStats: []
        });
        this.isFetching = false;
      }
    );
  }
}
